// Rollsidor för foto (yrkesroll + ort/kategori) — Etapp 2 i fotoplanen.
// Samma datamodell som src/content/foto-topics.ts och samma generator,
// men egna sökjobb: "porträttfotograf", "fotostudio", "företagsfotograf",
// "reklamfotograf", "personalfoto" och "bröllopsfotograf".

import type { FotoTopic } from "@/content/foto-topics";

const LEV = "Retuscherade bilder levereras normalt inom 3–5 arbetsdagar.";
const KONTAKT = "Kontakt: Fenny, hey@fenny.se.";

export const fotoRoleTopics: FotoTopic[] = [
  {
    slug: "portrattfotograf",
    nav: "Porträttfotograf",
    crumb: "Porträttfotograf",
    title: "Porträttfotograf",
    metaTitle: "Porträttfotograf i Stockholm och hela Sverige | Fenny Foto",
    description:
      "Porträttfotograf för företag och privatpersoner. Studio i Stockholm eller mobil studio hos er, leverans inom 3–5 arbetsdagar. Offert per uppdrag.",
    eyebrow: "Porträttfotograf",
    h1: "Porträttfotograf som får folk att se ut som sig själva.",
    lede:
      "Vi fotograferar porträtt för sajt, LinkedIn, pressrum och årsredovisning — i studio i Stockholm eller med mobil studio på er arbetsplats. Samma ljus, samma manér och ett schema som gör att ingen står och väntar.",
    question: "Vad gör en porträttfotograf och vad kostar det?",
    quickAnswer:
      "En porträttfotograf planerar ljus, bakgrund och beskärning så att en person blir igenkännbar och trovärdig i de format bilden ska användas i. Hos Fenny tar ett porträtt 5–15 minuter per person, en halvdag räcker till 10–20 personer och en heldag till 20–40. Priset offereras per uppdrag utifrån antal personer, retuschnivå och användningsrätt — inte per bild. " +
      LEV +
      " " +
      KONTAKT,
    serviceType: "Porträttfotografering",
    gallery: "portratt",
    galleryHeading: "Porträtt ur skarpa uppdrag",
    bullets: [
      { title: "Studio eller på plats", body: "Studio i Stockholm, eller mobil studio i ert konferensrum var som helst i landet." },
      { title: "5–15 minuter per person", body: "Tidsschema som håller — även när hela organisationen ska igenom på en dag." },
      { title: "Repeterbart manér", body: "Ljus och beskärning dokumenteras så att nyanställda matchar serien ett år senare." },
    ],
    sections: [
      {
        id: "typer-av-portratt",
        heading: "Tre porträttyper — och när ni behöver vilken",
        paragraphs: [
          "Headshot är tätt beskuret kring ansikte och axlar och gjort för små format: LinkedIn, intranät, signaturer. Miljöporträtt visar personen i sitt sammanhang och används på sajt, i presentationer och i rekrytering. Redaktionellt porträtt är friare och används i intervjuer, press och årsredovisning.",
          "De flesta bolag beställer headshot plus miljöporträtt i samma pass. Det förlänger dagen marginellt men fördubblar användbarheten i bildbanken.",
        ],
      },
      {
        id: "sa-jobbar-vi",
        heading: "Så jobbar en porträttfotograf i praktiken",
        paragraphs: [
          "Vi bygger ljus först och fotograferar sedan enligt tidsschema. Varje person får se ett par bilder direkt i skärmen, vilket är den enskilt viktigaste anledningen till att även de som ogillar kameran slappnar av.",
          "Efter dagen väljer ni i ett digitalt galleri och vi retuscherar urvalet varsamt: färg, ljus och tillfälligheter — inte ansiktsdrag.",
        ],
      },
      {
        id: "portratt-privat",
        heading: "Porträtt för privatpersoner",
        paragraphs: [
          "Vi fotograferar även enskilda porträtt för egenföretagare, författare, artister och experter som behöver en pressbild som håller i flera år.",
          "Ett enskilt porträttpass tar 45–90 minuter i studio och levereras med ett urval retuscherade bilder i stående, liggande och kvadratiskt format.",
        ],
      },
    ],
    faq: [
      { q: "Vad kostar en porträttfotograf?", a: "Priset sätts per pass, inte per bild. En halvdag räcker till 10–20 personer och en heldag till 20–40, vilket gör att kostnaden per person sjunker snabbt med antalet. Vi lämnar fast pris i offerten innan dagen bokas." },
      { q: "Var fotograferar ni?", a: "Studio i Stockholm eller mobil studio hos er. Vi arbetar i hela Sverige — resa inom Storstockholm ingår, längre resor specificeras i offerten." },
      { q: "Hur lång tid tar ett porträtt?", a: "5–10 minuter för ett headshot, 10–15 minuter om ett miljöporträtt ingår, och 45–90 minuter för ett enskilt porträttpass i studio." },
      { q: "Hur många bilder får vi?", a: "Normalt 2–4 retuscherade bilder per person, valda av er ur ett digitalt galleri. Fler bilder kan väljas till." },
      { q: "Vem äger bilderna?", a: "Ni får fri användningsrätt i egna kanaler. Upphovsrätten ligger kvar hos fotografen, och användning i köpt media eller stor tryckupplaga specificeras i offerten." },
    ],
    knowsAbout: ["Porträttfotograf", "Porträttfotografering", "Headshots", "Företagsporträtt", "Fotograf Stockholm"],
    related: [
      { label: "Headshots & LinkedIn-porträtt", to: "/foto/headshots" },
      { label: "Företagsporträtt", to: "/foto/foretagsportratt" },
      { label: "Vad kostar företagsporträtt?", to: "/foto/foretagsportratt-pris" },
      { label: "Porträttfotograf Stockholm", to: "/foto/portrattfotograf-stockholm" },
      { label: "Porträttfotograf Göteborg", to: "/foto/portrattfotograf-goteborg" },
      { label: "Porträttfotograf Malmö", to: "/foto/portrattfotograf-malmo" },
    ],
  },
  {
    slug: "fotostudio-stockholm",
    nav: "Fotostudio i Stockholm",
    crumb: "Fotostudio Stockholm",
    title: "Fotostudio i Stockholm",
    metaTitle: "Fotostudio i Stockholm — studiofotografering | Fenny",
    description:
      "Fotostudio i Stockholm för porträtt, produkt och kampanj. Fotograf och ljussättning ingår, eller mobil studio hos er. Leverans 3–5 arbetsdagar.",
    eyebrow: "Fotostudio Stockholm",
    h1: "Fotostudio i Stockholm — med fotograf, ljus och ett resultat som är klart samma dag.",
    lede:
      "Vi arbetar i studio i Stockholm för porträtt, produkt och kampanj, och tar med samma uppsättning ut till era lokaler när det passar bättre. Studio, ljus, fotograf och bildhantering ingår i uppdraget.",
    question: "Vad ingår i en fotostudio i Stockholm och vad kostar det?",
    quickAnswer:
      "Fenny arbetar i fotostudio i Stockholm där ljus, bakgrunder, fotograf och bildhantering ingår i uppdraget — vi hyr inte ut studion tom. En halvdag i studio räcker normalt till 10–20 porträtt eller 15–30 packshots, en heldag till ungefär dubbelt. Priset offereras per uppdrag utifrån antal bilder, retusch och användningsrätt. " +
      LEV +
      " " +
      KONTAKT,
    serviceType: "Studiofotografering",
    gallery: "studio",
    galleryHeading: "Bilder fotograferade i studio",
    bullets: [
      { title: "Kontrollerat ljus", body: "Samma ljus i januari som i juli — bilderna hänger ihop över tid." },
      { title: "Vit, grå eller kulör botten", body: "Frilagt mot vitt för e-handel, kulör botten för kampanj och profil." },
      { title: "Mobil studio ingår", body: "Behöver ni fotografera på kontoret tar vi med studion dit i stället." },
    ],
    sections: [
      {
        id: "vad-ingar",
        heading: "Vad ingår när ni bokar studio hos oss",
        paragraphs: [
          "Fotograf, ljussättning, bakgrunder, stativ och tethering till skärm så att ni ser bilderna medan de tas. Efter passet får ni ett digitalt galleri att välja ur, och det urvalet retuscheras.",
          "Vi hyr inte ut studion utan fotograf. Anledningen är enkel: det som gör studiobilder användbara är ljussättningen, och den vill vi ansvara för.",
        ],
      },
      {
        id: "vad-fotograferas",
        heading: "Vad som fungerar bäst i studio",
        paragraphs: [
          "Porträtt och headshots när hela organisationen ska ha samma manér. Produkter och packshots när bilderna ska ligga i e-handel eller katalog. Kampanjbilder när ljuset måste vara exakt och vädret inte får ha en åsikt.",
          "Miljöbilder, reportage och event fotograferas i stället på plats — där är verkligheten poängen.",
        ],
      },
      {
        id: "logistik",
        heading: "Logistik i Stockholm",
        paragraphs: [
          "Studion ligger i Stockholm och nås enkelt med kollektivtrafik. Produkter kan skickas i förväg och packas upp av oss inför passet.",
          "Ska hela teamet fotograferas är mobil studio ofta effektivare: vi bygger upp i ett konferensrum på 45 minuter och ni slipper skicka iväg 30 personer på stan.",
        ],
      },
    ],
    faq: [
      { q: "Kan vi hyra studion utan fotograf?", a: "Nej. Vi arbetar alltid med fotograf och ljussättning i uppdraget, eftersom det är det som avgör om bilderna blir användbara." },
      { q: "Vad kostar en halvdag i studio?", a: "Priset offereras per uppdrag utifrån antal bilder, retuschnivå och användningsrätt. En halvdag räcker normalt till 10–20 porträtt eller 15–30 packshots." },
      { q: "Kan ni fotografera hos oss i stället?", a: "Ja. Vi tar med mobil studio till era lokaler i Stockholm eller i övriga landet. Uppsättningen är densamma och bilderna hänger ihop med studiomaterialet." },
      { q: "Kan vi vara med under fotograferingen?", a: "Ja, och det rekommenderas. Bilderna visas på skärm under passet så att ni kan godkänna manéret direkt." },
      { q: "Hur skickar vi produkter?", a: "Skicka dem till studion senast dagen innan passet, så packar vi upp, rengör och förbereder dem inför fotograferingen." },
    ],
    knowsAbout: ["Fotostudio Stockholm", "Studiofotografering", "Packshots", "Porträttfotografering", "Fotograf Stockholm"],
    related: [
      { label: "Studiofotografering", to: "/foto/studiofotografering" },
      { label: "Produktfoto", to: "/foto/produktfoto" },
      { label: "Porträttfotograf Stockholm", to: "/foto/portrattfotograf-stockholm" },
      { label: "Produktfotograf Stockholm", to: "/foto/produktfotograf-stockholm" },
      { label: "Fotograf Stockholm", to: "/foto/stockholm" },
    ],
  },
  {
    slug: "foretagsfotograf",
    nav: "Företagsfotograf",
    crumb: "Företagsfotograf",
    title: "Företagsfotograf",
    metaTitle: "Företagsfotograf för hela bildbehovet | Fenny Foto",
    description:
      "Företagsfotograf för porträtt, miljöbilder, kampanj, produkt och event. En kontaktperson, ett bildmanér och offert per uppdrag. Leverans inom 3–5 arbetsdagar.",
    eyebrow: "Företagsfotograf",
    h1: "En företagsfotograf som kan hela bildbehovet — inte bara porträtten.",
    lede:
      "Fenny är fotografen som bolag anlitar när bilderna ska hänga ihop: porträtt, miljö, kampanj, produkt och event under ett manér, med samma person som ansvarar för planering, fotografering och leverans.",
    question: "Vad gör en företagsfotograf?",
    quickAnswer:
      "En företagsfotograf producerar bilderna ett bolag använder i sina egna kanaler: porträtt och headshots, miljöbilder från verksamheten, kampanj- och varumärkesbilder, produktbilder samt eventdokumentation. Skillnaden mot att anlita olika frilansare per uppdrag är att manéret hålls konsekvent över tid. Fenny offererar per uppdrag utifrån tid, antal bilder och användningsrätt. " +
      LEV +
      " " +
      KONTAKT,
    serviceType: "Företagsfotografering",
    gallery: "alla",
    galleryHeading: "Ett tvärsnitt ur företagsuppdragen",
    bullets: [
      { title: "En kontaktperson", body: "Fenny ansvarar för planering, fotografering och leverans — inga mellanhänder." },
      { title: "Ett manér", body: "Porträtt, miljö och kampanj fotograferas så att de fungerar sida vid sida." },
      { title: "Bilder som används", body: "Levereras sorterat i webb- och tryckformat, med filnamn ni hittar i." },
    ],
    sections: [
      {
        id: "vad-gor-en-foretagsfotograf",
        heading: "Vad en företagsfotograf faktiskt levererar",
        paragraphs: [
          "Ett bolags bildbehov är sällan en fotografering. Det är porträtt när någon börjar, miljöbilder när sajten görs om, produktbilder när sortimentet ändras och eventbilder några gånger per år.",
          "En företagsfotograf tar ansvar för att de här bilderna ser ut att komma från samma bolag — genom ett dokumenterat manér för ljus, färg och beskärning som återanvänds mellan tillfällena.",
        ],
      },
      {
        id: "roll-eller-tjanst",
        heading: "Företagsfotograf, fotoagentur eller frilans?",
        paragraphs: [
          "Frilans passar när behovet är ett enskilt uppdrag utan krav på kontinuitet. En företagsfotograf med löpande uppdrag passar när samma manér ska hålla över år.",
          "Behöver ni dessutom rörligt material eller flera fotografer samtidigt är det fotoagentur-upplägget som gäller — samma manér, större kapacitet.",
        ],
      },
      {
        id: "sa-borjar-vi",
        heading: "Så börjar ett samarbete",
        paragraphs: [
          "Vi går igenom vad bilderna ska användas till, vilka format som behövs och vad som saknas i dagens bildbank. Därefter kommer offert med upplägg, tid och pris inom en arbetsdag.",
          "Många börjar med en porträttdag och lägger till miljö- och kampanjbilder när manéret sitter.",
        ],
      },
    ],
    faq: [
      { q: "Vad kostar en företagsfotograf?", a: "Fenny arbetar med offert per uppdrag i stället för fast prislista. Priset styrs av tid på plats, antal bilder, retuschnivå och användningsrätt, och låses innan dagen bokas." },
      { q: "Kan vi anlita er löpande?", a: "Ja. Många bolag lägger 2–4 fotodagar per år, vilket täcker nyanställda, kampanjer och event. Upplägget är månadsvis utan bindningstid." },
      { q: "Vad skiljer er från en frilansfotograf?", a: "Kontinuiteten. Manér, ljusuppsättning och filstruktur dokumenteras, så att bilder tagna med ett års mellanrum fortfarande hänger ihop." },
      { q: "Fotograferar ni i hela Sverige?", a: "Ja. Vi utgår från Stockholm och arbetar i hela landet — resa specificeras i offerten." },
      { q: "Kan ni filma också?", a: "Ja. Fenny är i grunden ett filmproduktionsbolag, och vi kombinerar ofta foto och film på samma inspelningsdag." },
    ],
    knowsAbout: ["Företagsfotograf", "Företagsfotografering", "Bildmanér", "Fotograf för företag"],
    related: [
      { label: "Företagsfotografering", to: "/foto/foretagsfotografering" },
      { label: "Fotoagentur", to: "/foto/fotoagentur" },
      { label: "Vad kostar en fotograf?", to: "/foto/vad-kostar-en-fotograf" },
      { label: "Företagsfotograf Stockholm", to: "/foto/foretagsfotograf-stockholm" },
      { label: "Företagsfotograf Göteborg", to: "/foto/foretagsfotograf-goteborg" },
      { label: "Företagsfotograf Malmö", to: "/foto/foretagsfotograf-malmo" },
    ],
  },
  {
    slug: "reklamfotograf",
    nav: "Reklamfotograf",
    crumb: "Reklamfotograf",
    title: "Reklamfotograf",
    metaTitle: "Reklamfotograf för kampanj och annons | Fenny Foto",
    description:
      "Reklamfotograf för kampanj, annons och varumärke. Koncept, casting, produktion och retusch i de format kampanjen ska köras i. Offert per uppdrag.",
    eyebrow: "Reklamfotograf",
    h1: "Reklamfotograf som börjar i formatet, inte i kameran.",
    lede:
      "En kampanjbild ska fungera i liggande annons, i stående story och i en kvadrat. Vi planerar beskärningar, textytor och bildmanér innan vi fotograferar, så att kampanjen inte faller isär i produktion.",
    question: "Vad gör en reklamfotograf?",
    quickAnswer:
      "En reklamfotograf producerar bilder för kampanj, annons och varumärke — vilket innebär ansvar för koncept, ljus, casting, styling och beskärningar i de format kampanjen ska köras i. Hos Fenny planeras formaten före fotograferingen, så att samma bild fungerar i liggande annons, stående story och kvadrat. Priset offereras per uppdrag utifrån produktionens omfattning, medverkande och användningsrätt. " +
      KONTAKT,
    serviceType: "Reklamfotografering",
    gallery: "kampanj",
    galleryHeading: "Kampanj- och reklambilder",
    bullets: [
      { title: "Formaten först", body: "Vi fotograferar med kampanjens beskärningar och textytor inritade." },
      { title: "Casting och styling", body: "Medverkande, kläder och rekvisita hanteras som en del av produktionen." },
      { title: "Rättigheter i klartext", body: "Användningsrätt, medietyp och tid skrivs in i offerten — inga överraskningar." },
    ],
    sections: [
      {
        id: "koncept",
        heading: "Koncept innan kamera",
        paragraphs: [
          "Vi börjar med vad kampanjen ska få mottagaren att göra, vilka ytor bilden ska köras i och vilken känsla varumärket äger. Därefter byggs ett moodboard och en shotlist som alla kan godkänna innan produktionsdagen.",
          "Det gör att fotograferingsdagen handlar om utförande, inte om att komma på idéer under tidspress.",
        ],
      },
      {
        id: "produktion",
        heading: "Produktionsdagen",
        paragraphs: [
          "Ljussättning, medverkande, styling och produkter hanteras enligt körschema. Bilderna visas löpande på skärm så att beslut fattas på plats.",
          "Vi fotograferar medvetet med luft i bilden där text ska ligga, och tar alternativa beskärningar av varje nyckelbild.",
        ],
      },
      {
        id: "efterarbete",
        heading: "Retusch och leverans",
        paragraphs: [
          "Retusch sker i två steg: urval och grovretusch först, finretusch på de bilder som faktiskt går i produktion.",
          "Leverans sker i alla efterfrågade beskärningar och i både webb- och tryckformat, med tydliga filnamn per placering.",
        ],
      },
    ],
    faq: [
      { q: "Vad kostar en reklamfotograf?", a: "Priset offereras per produktion och styrs av antal bilder, medverkande, styling, plats och användningsrätt. Vi lämnar fast pris innan produktionen bokas." },
      { q: "Ingår modeller och casting?", a: "Vi hanterar casting och bokning när det behövs. Modellarvoden och rättigheter specificeras separat i offerten." },
      { q: "Hur länge får vi använda bilderna?", a: "Användningsrätt skrivs in i offerten — kanaler, medietyp, geografi och tid. Egna kanaler är oftast obegränsat i tid, köpt media tidsbegränsas." },
      { q: "Kan ni göra rörligt samtidigt?", a: "Ja. Att fotografera stillbilder och filma på samma dag är ofta den billigaste vägen till ett komplett kampanjpaket." },
      { q: "Vem äger originalfilerna?", a: "Ni får de levererade bilderna med avtalad användningsrätt. Råfiler ingår normalt inte, men kan avtalas separat." },
    ],
    knowsAbout: ["Reklamfotograf", "Kampanjfoto", "Annonsproduktion", "Varumärkesbilder"],
    related: [
      { label: "Kampanjfoto", to: "/foto/kampanjfoto" },
      { label: "Reklamfilm", to: "/reklamfilm" },
      { label: "Produktfotograf Stockholm", to: "/foto/produktfotograf-stockholm" },
      { label: "Produktfotograf Göteborg", to: "/foto/produktfotograf-goteborg" },
    ],
  },
  {
    slug: "personalfoto",
    nav: "Personalfoto",
    crumb: "Personalfoto",
    title: "Personalfoto",
    metaTitle: "Personalfoto — hela personalen på en dag | Fenny Foto",
    description:
      "Personalfoto för sajt, intranät och rekrytering. Mobil studio hos er, 20–40 personer per dag, samma manér för alla och leverans inom 3–5 arbetsdagar.",
    eyebrow: "Personalfoto",
    h1: "Personalfoto där alla ser ut att jobba på samma ställe.",
    lede:
      "Vi fotograferar hela personalen på en dag i era lokaler, med samma ljus, samma bakgrund och samma beskärning — och dokumenterar uppsättningen så att nyanställda kan matchas in senare.",
    question: "Hur går personalfotografering till?",
    quickAnswer:
      "Personalfotografering går till så att fotografen bygger mobil studio i ett konferensrum, cirka tre gånger tre meter med ett eluttag, och fotograferar enligt tidsschema med 5–10 minuter per person. En dag räcker normalt till 20–40 medarbetare. Ljusuppsättningen dokumenteras så att nyanställda kan fotograferas in i samma serie senare. " +
      LEV +
      " " +
      KONTAKT,
    serviceType: "Personalfotografering",
    gallery: "portratt",
    galleryHeading: "Personalporträtt ur skarpa uppdrag",
    bullets: [
      { title: "20–40 personer per dag", body: "Tidsschema per avdelning så att verksamheten kan rulla vidare." },
      { title: "Ingen behöver resa", body: "Vi kommer till kontoret — mobil studio riggas på 45 minuter." },
      { title: "Nyanställda matchas in", body: "Uppsättningen dokumenteras och kan återskapas exakt vid komplettering." },
    ],
    sections: [
      {
        id: "forberedelse",
        heading: "Förberedelse: schema och klädguide",
        paragraphs: [
          "Vi sätter ett tidsschema tillsammans med er, oftast i femminutersluckor per person, och skickar en kort klädguide som gör att bilderna hänger ihop utan att någon behöver klä upp sig.",
          "Ett rum på ungefär tre gånger tre meter med ett eluttag räcker. Fönster är trevligt men inget krav — vi tar med eget ljus.",
        ],
      },
      {
        id: "dagen",
        heading: "Fotograferingsdagen",
        paragraphs: [
          "Varje person får se ett par bilder direkt i skärmen. Det tar bort nervositeten och gör att vi kan justera i stället för att hoppas.",
          "Vill ni ha både tight headshot och friare miljöporträtt kör vi båda i samma lucka — det tar ungefär fem minuter extra per person.",
        ],
      },
      {
        id: "efterat",
        heading: "Urval, retusch och leverans",
        paragraphs: [
          "Ni väljer bilder i ett digitalt galleri, antingen centralt eller genom att varje medarbetare väljer sin egen. Urvalet retuscheras varsamt.",
          "Leverans sker i kvadratiskt format för intranät och LinkedIn, stående för sajten och i högupplöst format för tryck.",
        ],
      },
    ],
    faq: [
      { q: "Vad kostar personalfoto per person?", a: "Personalfoto prissätts per dag eller halvdag, inte per person, vilket gör att kostnaden per medarbetare sjunker med antalet. En halvdag räcker till 10–20 personer och en heldag till 20–40." },
      { q: "Vad behöver vi ordna?", a: "Ett rum på cirka tre gånger tre meter, ett eluttag och ett tidsschema. Vi tar med ljus, bakgrund och kamera." },
      { q: "Måste alla vara med?", a: "Nej, men bilderna fungerar bäst när hela avdelningen har samma manér. Vi kan komplettera med en kortare uppföljning för dem som var frånvarande." },
      { q: "Hur hanteras GDPR?", a: "Ni är personuppgiftsansvariga för publiceringen. Vi rekommenderar ett kort medgivande per medarbetare och raderar arbetsfiler efter avtalad tid." },
      { q: "Kan vi göra det på flera kontor?", a: "Ja. Samma uppsättning körs på varje ort så att bilderna hänger ihop oavsett var personen sitter." },
    ],
    knowsAbout: ["Personalfoto", "Personalfotografering", "Företagsporträtt", "Mobil studio", "GDPR bilder"],
    related: [
      { label: "Företagsporträtt", to: "/foto/foretagsportratt" },
      { label: "Headshots", to: "/foto/headshots" },
      { label: "Vad kostar företagsporträtt?", to: "/foto/foretagsportratt-pris" },
      { label: "Företagsfotograf", to: "/foto/foretagsfotograf" },
      { label: "Porträttfotograf Göteborg", to: "/foto/portrattfotograf-goteborg" },
      { label: "Porträttfotograf Malmö", to: "/foto/portrattfotograf-malmo" },
    ],
  },
  {
    slug: "brollopsfotograf",
    nav: "Bröllopsfotograf",
    crumb: "Bröllopsfotograf",
    title: "Bröllopsfotograf",
    metaTitle: "Bröllopsfotograf i Stockholm — foto och film | Fenny",
    description:
      "Bröllopsfotograf som arbetar dokumentärt och kan kombineras med bröllopsfilm samma dag. Ett team, ett manér, ett pris. Offert per bröllop.",
    eyebrow: "Bröllopsfotograf",
    h1: "Bröllopsfotograf — och filmteamet står redan bredvid.",
    lede:
      "Vi fotograferar bröllop dokumentärt: dagen som den var, inte som en uppställd katalog. Eftersom Fenny också gör bröllopsfilm kan foto och film bokas som ett uppdrag, med ett team som redan vet var den andra står.",
    question: "Vad kostar en bröllopsfotograf och vad ingår?",
    quickAnswer:
      "En bröllopsfotograf följer dagen från förberedelser till fest och levererar ett redigerat urval bilder. Hos Fenny offereras varje bröllop utifrån antal timmar, antal fotografer och om bröllopsfilm ingår — foto och film på samma dag är billigare än två separata bokningar eftersom teamet delar planering och resa. Bilderna levereras i ett digitalt galleri. " +
      KONTAKT,
    serviceType: "Bröllopsfotografering",
    gallery: "alla",
    galleryHeading: "Bilder ur vårt arbete",
    bullets: [
      { title: "Dokumentärt först", body: "Vi styr så lite som möjligt och fotograferar det som faktiskt händer." },
      { title: "Foto och film ihop", body: "Samma team, samma dag, ett pris — och materialet hänger ihop." },
      { title: "Galleri att dela", body: "Bilderna levereras i ett digitalt galleri ni kan dela med gäster." },
    ],
    sections: [
      {
        id: "upplagg",
        heading: "Upplägg: från förberedelser till fest",
        paragraphs: [
          "Vi börjar oftast under förberedelserna, följer vigsel och mingel och stannar in i festen så länge det är avtalat. Porträtt av brudparet tas i ett kort pass när ljuset är som bäst — sällan mer än trettio minuter.",
          "Ett fåtal gruppbilder planeras i förväg med namn och ordning, så att det tar tio minuter i stället för en timme.",
        ],
      },
      {
        id: "foto-och-film",
        heading: "Varför foto och film samma dag",
        paragraphs: [
          "När foto och film bokas separat konkurrerar teamen om samma ögonblick och samma positioner. Bokas de ihop planeras dagen en gång, och stillbilder och rörligt kompletterar varandra i stället för att krocka.",
          "Det blir också billigare: planering, resa och recce görs en gång.",
        ],
      },
      {
        id: "leverans",
        heading: "Leverans",
        paragraphs: [
          "Ni får ett redigerat urval bilder i ett digitalt galleri, i format för både skärm och tryck. Antalet beror på dagens längd och avtalas i offerten.",
          "Väljer ni film också levereras filmen separat enligt överenskommet upplägg.",
        ],
      },
    ],
    faq: [
      { q: "Vad kostar en bröllopsfotograf?", a: "Priset offereras per bröllop och styrs av antal timmar, antal fotografer, resa och om film ingår. Vi lämnar fast pris innan datumet bokas." },
      { q: "Kan vi boka både foto och film?", a: "Ja, och det är det vanligaste hos oss. Foto och film samma dag planeras som ett uppdrag, vilket både sänker priset och gör att materialet hänger ihop." },
      { q: "Hur många bilder får vi?", a: "Ett redigerat urval som speglar hela dagen. Antalet avtalas i offerten och beror på hur många timmar vi följer er." },
      { q: "Hur långt i förväg bör vi boka?", a: "Populära lördagar i juni till augusti bokas ofta 9–12 månader i förväg. Hör av er tidigt så håller vi datumet." },
      { q: "Reser ni utanför Stockholm?", a: "Ja, vi fotograferar bröllop i hela Sverige. Resa och eventuell övernattning specificeras i offerten." },
    ],
    knowsAbout: ["Bröllopsfotograf", "Bröllopsfotografering", "Bröllopsfilm", "Dokumentärt bröllopsfoto"],
    related: [
      { label: "Bröllopsfilm", to: "/brollopsfilm" },
      { label: "Foto och film ihop", to: "/foto/foto-och-film" },
      { label: "Porträttfotograf", to: "/foto/portrattfotograf" },
      { label: "Fotograf Stockholm", to: "/foto/stockholm" },
    ],
  },
];
